import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { IdParamsType } from 'utils/types';
import { getData } from 'services/api';
import { sendGAEvent } from 'utils/utils';
import { updateEvent } from 'features/PunchupPages/EditEvent/services';
import { Event } from 'features/PunchupPages/EditEvent/types';

export const useGeneralSection = () => {
	const { id }: IdParamsType = useParams();
	const queryClient = useQueryClient();

	const { data: event } = useQuery({
		queryKey: ['event', id],
		queryFn: (): Promise<Event> => getData(`/events/${id}`),
		select: (eventData: Event) => ({
			...eventData,
			custom_fees: eventData.custom_fees.map(({ taxable, general, ...rest }) => ({
				...rest,
				taxable: taxable ? '1' : '0',
				general: general ? '1' : '0',
			})),
		}),
	});

	const { mutateAsync: editEvent, isLoading: isSubmitting } = useMutation<Event, unknown, any>(
		async (variables: any) => {
			const response = await updateEvent(variables);
			return response.data as Event;
		},
		{
			onSuccess: (updatedEvent: Event) => {
				// Downstroke's `event-updated` handler reads only `eventId` (see docs/iframe-communication.md);
				// the breadcrumb title is refreshed via the dedicated `event-name-updated` message.
				window.parent.postMessage({ action: 'event-updated', eventId: id }, '*');
				window.parent.postMessage({ action: 'event-name-updated', name: updatedEvent?.name }, '*');
				sendGAEvent({ category: 'Journeys', action: 'Edit event' });
				queryClient.invalidateQueries(['event', id]);
			},
		}
	);

	return { event, editEvent, isSubmitting, eventId: id };
};
